import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faPhone,
  faBuilding,
  faShield,
  faKey,
  faPencil,
  faCheck,
  faTimes,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);
  const [formData, setFormData] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    department: user?.department || "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const requiresCurrentPassword = Boolean(user?.requiresCurrentPassword);
  const hasPassword = Boolean(user?.hasPassword);

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || "",
        email: user.email || "",
        phone: user.phone || "",
        department: user.department || "",
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (
      formData.phone &&
      !/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ""))
    ) {
      newErrors.phone = "Phone number is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (requiresCurrentPassword && !passwordData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const result = await updateProfile(formData);

      if (result.success) {
        setSuccessMessage("Profile updated successfully!");
        setEditMode(false);
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setErrors({
          general:
            result.error || "Failed to update profile. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrors({ general: "Failed to update profile. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    try {
      setLoading(true);
      const result = await changePassword(
        passwordData.currentPassword || "",
        passwordData.newPassword,
      );

      if (result.success) {
        setSuccessMessage("Password changed successfully!");
        setPasswordMode(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setTimeout(() => setSuccessMessage(""), 5000);
      } else {
        setErrors({
          general:
            result.error || "Failed to change password. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setErrors({ general: "Failed to change password. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setFormData({
      fullName: user?.fullName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      department: user?.department || "",
    });
    setErrors({});
  };

  const cancelPasswordChange = () => {
    setPasswordMode(false);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordVisibility({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setErrors({});
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      admin: "Administrator",
      manager: "Manager",
      engineer: "Engineer",
      inspector: "Inspector",
      viewer: "Viewer",
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      admin: "bg-red-100 text-red-800",
      manager: "bg-blue-100 text-blue-800",
      engineer: "bg-green-100 text-green-800",
      inspector: "bg-yellow-100 text-yellow-800",
      viewer: "bg-gray-100 text-gray-800",
    };
    return colorMap[role] || "bg-gray-100 text-gray-800";
  };

  const formatDisplayDate = (value) => {
    if (!value) {
      return "N/A";
    }

    const normalized =
      typeof value === "string" ? value.replace(" ", "T") : value;
    const parsed = new Date(normalized);

    if (Number.isNaN(parsed.getTime())) {
      return "N/A";
    }

    return parsed.toLocaleDateString();
  };

  const memberSinceValue = user?.createdAt || user?.created_at;

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon
                icon={faCheck}
                className="h-5 w-5 text-green-400"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon
                icon={faTimes}
                className="h-5 w-5 text-red-400"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {errors.general}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Profile Information
                  </h3>
                  {!editMode ? (
                    <button
                      onClick={() => setEditMode(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <FontAwesomeIcon
                        icon={faPencil}
                        className="h-4 w-4 mr-1"
                      />
                      Edit
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleProfileUpdate}
                        disabled={loading}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                        ) : (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="h-4 w-4 mr-1"
                          />
                        )}
                        {loading ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <FontAwesomeIcon
                          icon={faTimes}
                          className="h-4 w-4 mr-1"
                        />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6">
                <form onSubmit={handleProfileUpdate}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon
                          icon={faUser}
                          className="h-4 w-4 inline mr-2"
                        />
                        Full Name
                      </label>
                      {editMode ? (
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                            errors.fullName
                              ? "border-red-300"
                              : "border-gray-300"
                          }`}
                        />
                      ) : (
                        <p className="text-gray-900">
                          {user.fullName || "Not provided"}
                        </p>
                      )}
                      {errors.fullName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="h-4 w-4 inline mr-2"
                        />
                        Email
                      </label>
                      {editMode ? (
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                            errors.email ? "border-red-300" : "border-gray-300"
                          }`}
                        />
                      ) : (
                        <p className="text-gray-900">{user.email}</p>
                      )}
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon
                          icon={faPhone}
                          className="h-4 w-4 inline mr-2"
                        />
                        Phone
                      </label>
                      {editMode ? (
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                            errors.phone ? "border-red-300" : "border-gray-300"
                          }`}
                          placeholder="+1 (555) 123-4567"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {user.phone || "Not provided"}
                        </p>
                      )}
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FontAwesomeIcon
                          icon={faBuilding}
                          className="h-4 w-4 inline mr-2"
                        />
                        Department
                      </label>
                      {editMode ? (
                        <input
                          type="text"
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., Engineering, Inspection"
                        />
                      ) : (
                        <p className="text-gray-900">
                          {user.department || "Not assigned"}
                        </p>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Password Change */}
            <div className="mt-8 bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Change Password
                  </h3>
                  {!passwordMode ? (
                    <button
                      onClick={() => setPasswordMode(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <FontAwesomeIcon icon={faKey} className="h-4 w-4 mr-1" />
                      Change
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handlePasswordUpdate}
                        disabled={loading}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                        ) : (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="h-4 w-4 mr-1"
                          />
                        )}
                        {loading ? "Updating..." : "Update"}
                      </button>
                      <button
                        onClick={cancelPasswordChange}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <FontAwesomeIcon
                          icon={faTimes}
                          className="h-4 w-4 mr-1"
                        />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6">
                {passwordMode ? (
                  <form onSubmit={handlePasswordChange}>
                    <div className="space-y-4">
                      {requiresCurrentPassword ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
                          </label>
                          <div className="relative">
                            <input
                              type={
                                passwordVisibility.currentPassword
                                  ? "text"
                                  : "password"
                              }
                              name="currentPassword"
                              value={passwordData.currentPassword}
                              onChange={handlePasswordChange}
                              className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                                errors.currentPassword
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                            />
                            <button
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() =>
                                togglePasswordVisibility("currentPassword")
                              }
                            >
                              {passwordVisibility.currentPassword ? (
                                <FontAwesomeIcon
                                  icon={faEyeSlash}
                                  className="h-5 w-5 text-gray-400 hover:text-gray-600"
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="h-5 w-5 text-gray-400 hover:text-gray-600"
                                />
                              )}
                            </button>
                          </div>
                          {errors.currentPassword && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.currentPassword}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                          {hasPassword
                            ? "This account uses Firebase-managed authentication. Enter a new password below to update it."
                            : "No password is set for this account yet. Choose a new password below."}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={
                              passwordVisibility.newPassword
                                ? "text"
                                : "password"
                            }
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                              errors.newPassword
                                ? "border-red-300"
                                : "border-gray-300"
                            }`}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() =>
                              togglePasswordVisibility("newPassword")
                            }
                          >
                            {passwordVisibility.newPassword ? (
                              <FontAwesomeIcon
                                icon={faEyeSlash}
                                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faEye}
                                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                              />
                            )}
                          </button>
                        </div>
                        {errors.newPassword && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.newPassword}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={
                              passwordVisibility.confirmPassword
                                ? "text"
                                : "password"
                            }
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                              errors.confirmPassword
                                ? "border-red-300"
                                : "border-gray-300"
                            }`}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() =>
                              togglePasswordVisibility("confirmPassword")
                            }
                          >
                            {passwordVisibility.confirmPassword ? (
                              <FontAwesomeIcon
                                icon={faEyeSlash}
                                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faEye}
                                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                              />
                            )}
                          </button>
                        </div>
                        {errors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>
                  </form>
                ) : (
                  <p className="text-gray-500">
                    Click the Change button to update your password.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Account Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Account Summary
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="h-5 w-5 text-blue-600"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Username
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {user.username}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FontAwesomeIcon
                      icon={faShield}
                      className="h-5 w-5 text-green-600"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Role</p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}
                    >
                      {getRoleDisplay(user.role)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="h-5 w-5 text-purple-600"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                {user.lastLogin && (
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="h-5 w-5 text-yellow-600"
                      />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">
                        Last Login
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDisplayDate(user.lastLogin)}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="h-5 w-5 text-gray-600"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Member Since
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatDisplayDate(memberSinceValue)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
